import type { Quote } from './Quote'

/**
 * Formats an amount in the unit of the job's Legend.
 */
export type QuoteUnitFormatter = (value: number) => string

/**
 * Summary item.
 */
export interface SummaryItem {
  type: 'Item'
  /**
   * The item label.
   */
  label: string
  /**
   * The item value.
   */
  value: string
  /**
   * Whether the item is highlighted.
   */
  highlighted?: boolean
}

/**
 * Divider.
 */
export interface SummaryDivider {
  type: 'Divider'
}

/**
 * Pricing information, formatted for display.
 */
export interface PricingInfo {
  /**
   * The total price.
   */
  total: string
  /**
   * The amount due now.
   */
  amountDueNow: string
  /**
   * The discount amount, empty when there is none.
   */
  discount: string
  /**
   * The primary amount.
   */
  primaryAmount: string
}

/**
 * Formats the quote's price for display.
 *
 * @param quote
 * @param formatUnit
 */
export function formatQuotePricing(
  quote: Quote,
  formatUnit: QuoteUnitFormatter,
): PricingInfo {
  const { price } = quote
  const discountAmount = price.discount?.amount ?? 0
  return {
    total: formatUnit(price.total),
    amountDueNow: formatUnit(price.upfrontAmount),
    discount: discountAmount === 0 ? '' : formatUnit(discountAmount),
    primaryAmount: formatUnit(price.primaryAmount),
  }
}

/**
 * Formats the quote as summary items.
 *
 * @param quote
 * @param formatUnit
 */
export function formatQuoteSummaryItems(
  quote: Quote,
  formatUnit: QuoteUnitFormatter,
): (SummaryItem | SummaryDivider)[] {
  const items: (SummaryItem | SummaryDivider)[] = []

  if (quote.items.length > 0) {
    items.push(
      {
        type: 'Item',
        label: 'Job Summary',
        value: quote.items.map((i) => i.name).join(`\n`),
      },
      { type: 'Divider' },
    )
  }

  if (quote.price.primaryAmount !== 0) {
    items.push({
      type: 'Item',
      label: 'Service Amount',
      highlighted: true,
      value: formatUnit(quote.price.primaryAmount),
    })
  }

  return filterSummaryItems(items)
}

/**
 * Filters the summary items so we never:
 *   1. start with a divider
 *   2. end with a divider
 *   3. have consecutive dividers
 *
 * @param items
 */
function filterSummaryItems(items: (SummaryItem | SummaryDivider)[]) {
  const lastIndex = items.length - 1
  return items.filter((item, index) => {
    if (item.type !== 'Divider') {
      return true
    }

    if (index === 0 || index === lastIndex) {
      return false
    }

    // Not the last item, so there is a next one.
    return items[index + 1].type !== 'Divider'
  })
}
